import {
    useCallback, useEffect, useId, useRef,
} from 'react';
import {
    type HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel,
} from '@microsoft/signalr';
import { useDispatch, useInstanceSelector } from '@s20/hooks/redux.hooks';
import { getHubByUrl } from '@s20/selectors/features/signalr.selectors';
import {
    addHub, addToHub, removeHub, updateHubState,
} from '@s20/slices/features/signalr.slice';

const webLockAPISupported = (): boolean => typeof navigator !== 'undefined' && Boolean(navigator.locks) && Boolean(Promise.withResolvers);

/**
 * Generates a held web lock that can be used to ensure a tab doesn't sleep
 * Should be used in conjunction with useSignalR to ensure the connection doesn't fail
 */
export const useRunningWebLock = (lockName: string, options?: LockOptions) => {
    const resolversRef = useRef<PromiseWithResolvers<unknown> | null>(null);
    const optionsRef = useRef(options);

    useEffect(() => {
        optionsRef.current = options;
    });

    const requestLock = useCallback(async (name: string, resolvers: PromiseWithResolvers<unknown>) => {
        try {
            await navigator.locks.request(
                name,
                optionsRef.current || { mode: 'shared' },
                () => resolvers.promise,
            );
        } catch (e) {
            console.log('Error requesting web lock', { e });
        }
    }, []);

    useEffect(() => {
        if (webLockAPISupported()) {
            if (!resolversRef.current) {
                resolversRef.current = Promise.withResolvers();
            }

            void(requestLock(lockName, resolversRef.current));
        }

        return () => {
            if (resolversRef.current) {
                resolversRef.current.resolve(true);
                resolversRef.current = null;
            }
        };
    }, [lockName]);

    return undefined;
};

interface SignalROptions {
    readonly onReconnect: (hub: HubConnection, connectionId?: string | undefined) => void;
}

/**
 * Generates and manages a SignalR hub that should reconnect automatically
 */
export const useSignalR = (hubUrl: string, options?: SignalROptions): HubConnection | null => {
    const uniqueId = useId();

    const hub = useInstanceSelector(getHubByUrl, hubUrl);
    const dispatch = useDispatch();

    const optionsRef = useRef(options);

    useEffect(() => {
        optionsRef.current = options;
    });

    useEffect(() => {
        if (!hub) {
            const newConnection = new HubConnectionBuilder()
                .withUrl(hubUrl)
                .configureLogging(LogLevel.Error)
                .withAutomaticReconnect()
                .withStatefulReconnect()
                .build();

            dispatch(addHub({
                id: hubUrl,
                uuid: uniqueId,
                hub: newConnection,
            }));

            const startHub = async () => {
                try {
                    await newConnection.start().catch((e) => console.log(e));
                    dispatch(updateHubState({ id: hubUrl, hubState: HubConnectionState.Connected }));
                } catch (e) {
                    console.log('error!', e);
                }
            };

            void startHub();

            newConnection.onclose((e) => {
                console.log('connection closed', e);
                dispatch(removeHub(uniqueId));
            });

            newConnection.onreconnected((connectionId) => {
                dispatch(updateHubState({ id: hubUrl, hubState: HubConnectionState.Connected }));

                if (optionsRef.current?.onReconnect) {
                    optionsRef.current.onReconnect(newConnection, connectionId);
                }
            });

            newConnection.onreconnecting(() => {
                dispatch(updateHubState({ id: hubUrl, hubState: HubConnectionState.Reconnecting }));
            });
        } else if (!hub.uuids.includes(uniqueId)) {
            dispatch(addToHub({
                id: hubUrl,
                uuid: uniqueId,
            }));
        }

        return () => {
            dispatch(removeHub(uniqueId));
        };
    }, [hubUrl]);

    if (hub?.hubState === HubConnectionState.Connected && hub?.hub) {
        return hub?.hub;
    }

    return null;
};